import React from "react";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Save,
  Download,
  Check,
  Loader2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useSlipLotById,
  useSubmitSlipLot,
  useExportSlipLotErrors,
} from "../../../../hooks";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import SlipLotDetailsDashboard from "../dashboard/dashboard";
import { ManualTab } from "../tabComponents/manual/manual";
import { BarcodeTab } from "../tabComponents/barcode/barcode";
import { TimelineTab } from "../../../shared/TimelineTab";
import { SubmitLotModal } from "../components/SubmitLotModal";
import { PageHeader } from "@shared/components/PageHeader";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export const SlipLotDetailsLanding = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const [isSubmitModalOpen, setIsSubmitModalOpen] = React.useState(false);

  const { data, isLoading } = useSlipLotById(id!);
  const slipLot = data?.result;
  const submitLotMutation = useSubmitSlipLot();
  const exportErrorsMutation = useExportSlipLotErrors(id!);

  const isLocked = Boolean(
    slipLot?.statusDisplay && slipLot.statusDisplay !== "Under Entry",
  );

  const disableAddEntry = Boolean(
    isLocked || (slipLot && slipLot.enteredCount >= slipLot.physicalSlipCount),
  );

  const handleExportErrors = () => {
    exportErrorsMutation.mutate(undefined, {
      onSuccess: (blobData: any) => {
        const blob = new Blob([blobData], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `slip_lot_errors_${slipLot?.lotId || id}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast({
          title: "Success",
          description: "Slip lot errors exported successfully.",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message ||
            "Failed to export slip lot errors.",
          variant: "destructive",
        });
      },
    });
  };

  const tabComponents = {
    manual: {
      label: "Manual",
      component: (
        <ManualTab
          data={slipLot}
          isLocked={isLocked}
          disableAddEntry={disableAddEntry}
        />
      ),
    },
    barcode: {
      label: "Barcode Scan (beta)",
      component: (
        <BarcodeTab isLocked={isLocked} disableAddEntry={disableAddEntry} />
      ),
    },
  };

  const urlMapping = {
    manual: "manual",
    barcode: "barcode-scan",
  };

  const getStatusBadge = (status: string) => {
    let color = "bg-gray-100 text-gray-800";
    let dot = "bg-gray-500";

    if (status === "Under Entry") {
      color = "bg-blue-50 text-blue-700 border-blue-200";
      dot = "bg-blue-500";
    } else if (status === "Partially Posted") {
      color = "bg-orange-50 text-orange-700 border-orange-200";
      dot = "bg-orange-500";
    } else if (status === "Fully Posted" || status === "Reconciled") {
      color = "bg-green-50 text-green-700 border-green-200";
      dot = "bg-green-500";
    } else if (status === "Closed") {
      color = "bg-purple-50 text-purple-700 border-purple-200";
      dot = "bg-purple-500";
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${color}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>
        {status}
      </span>
    );
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/cx/payments/slip-lots")}
          />

          {/* Page Header */}
          <div className="flex flex-col">
            <PageHeader
              title={`Slip Lot Details`}
              subtitle={`View breakdown and variances for slip lot`}
            />
          </div>
        </div>

        {/* Export Button */}
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportErrors}
            disabled={exportErrorsMutation.isPending}
          >
            {exportErrorsMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export Errors
          </Button>
          <Button
            size="sm"
            variant="default"
            onClick={() => setIsSubmitModalOpen(true)}
            disabled={isLocked}
          >
            <Check className="w-4 h-4 mr-2" />
            Submit Lot
          </Button>
        </div>
      </div>
      {/* File Info Card */}
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm relative">
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-amber-600"></div>
        <div className="p-4 pl-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">
                {slipLot?.lotId || "N/A"}
              </h1>
              {getStatusBadge(slipLot?.statusDisplay || "N/A")}
              {slipLot?.countMismatch > 0 && (
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  Count mismatch: {slipLot.countMismatch} missing
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 font-medium">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                Payment Centre ({slipLot?.centreCode || "N/A"})
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                Collection {slipLot?.collectionDate || "N/A"}
              </div>
              <div className="flex items-center gap-1.5">
                <User className="h-4 w-4" />
                {slipLot?.createdBy || "N/A"}
              </div>
              <div>
                Physical count:{" "}
                <span className="text-gray-900 font-bold">
                  {slipLot?.physicalSlipCount || 0}
                </span>
              </div>
              <div>
                Mode:{" "}
                <span className="text-gray-900 font-bold capitalize">
                  {slipLot?.entryMode || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard */}
      <SlipLotDetailsDashboard data={slipLot} />

      {/* Tabs */}
      <Tabs
        key={slipLot?.entryMode === "Barcode scan" ? "barcode" : "manual"}
        defaultValue={
          slipLot?.entryMode === "Barcode scan" ? "barcode" : "manual"
        }
        className="w-full mt-6"
        tabsListClassName="grid grid-cols-2"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        level={0}
      />

      {/* Audit Trail Section */}
      <div className="pt-2">
        <TimelineTab
          remoteUtilityId={remoteUtilityId}
          requestId={`${slipLot?.lotId || id}`}
        />
      </div>

      {/* Modals */}
      <SubmitLotModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        onSubmit={() => {
          if (id) submitLotMutation.mutate(id);
        }}
        data={slipLot}
      />
    </div>
  );
};

export default SlipLotDetailsLanding;
